import { FC, useRef, useState } from 'react'

import styled from 'styled-components'
import {
  ExpressCheckoutElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js'
import {
  ExpressPaymentType,
  StripeExpressCheckoutElementAvailablePaymentMethodsChangeEvent,
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementReadyEvent,
} from '@stripe/stripe-js'

import { PaymentMethodEnum, WalletPaymentMethod } from 'src/Constants/enums'

import { getWalletButtonOptions } from './walletButtonAppearance'

const Wrapper = styled.div`
  width: 100%;
`

export interface WalletCheckoutSession {
  clientSecret: string
  returnUrl: string
}

export interface WalletExpressCheckoutFormProps {
  onConfirmWalletPayment: (
    expressPaymentType: ExpressPaymentType,
  ) => Promise<WalletCheckoutSession | null>
  onWalletPaymentSuccess: () => void
  onWalletPaymentError: () => void
  onWalletProcessingChange?: (isProcessing: boolean) => void
  onWalletAvailabilityChange?: (methods: WalletPaymentMethod[]) => void
  selectedWalletMethod?: WalletPaymentMethod
}

interface Props extends WalletExpressCheckoutFormProps {
  resolvedTheme: 'light' | 'dark'
}

const getAvailableWalletMethods = (
  availablePaymentMethods: StripeExpressCheckoutElementReadyEvent['availablePaymentMethods'],
): WalletPaymentMethod[] => {
  if (!availablePaymentMethods) {
    return []
  }

  const methods: WalletPaymentMethod[] = []

  if (availablePaymentMethods.applePay) {
    methods.push(PaymentMethodEnum.APPLE_PAY)
  }
  if (availablePaymentMethods.googlePay) {
    methods.push(PaymentMethodEnum.GOOGLE_PAY)
  }

  return methods
}

const getChangedWalletMethods = (
  paymentMethods: StripeExpressCheckoutElementAvailablePaymentMethodsChangeEvent['paymentMethods'],
): WalletPaymentMethod[] => {
  if (!paymentMethods) {
    return []
  }

  const methods: WalletPaymentMethod[] = []

  if (paymentMethods.applePay?.available) {
    methods.push(PaymentMethodEnum.APPLE_PAY)
  }
  if (paymentMethods.googlePay?.available) {
    methods.push(PaymentMethodEnum.GOOGLE_PAY)
  }

  return methods
}

export const WalletExpressCheckoutForm: FC<Props> = ({
  resolvedTheme,
  onConfirmWalletPayment,
  onWalletPaymentSuccess,
  onWalletPaymentError,
  onWalletProcessingChange,
  onWalletAvailabilityChange,
  selectedWalletMethod,
}) => {
  const stripe = useStripe()
  const elements = useElements()
  const hasSeenWalletRef = useRef(false)
  const [isAvailabilityReported, setIsAvailabilityReported] = useState(false)

  const reportAvailability = (methods: WalletPaymentMethod[]): void => {
    setIsAvailabilityReported(true)

    if (methods.length) {
      hasSeenWalletRef.current = true
      onWalletAvailabilityChange?.(methods)

      return
    }

    if (!hasSeenWalletRef.current) {
      onWalletAvailabilityChange?.([])
    }
  }

  const handleReady = (event: StripeExpressCheckoutElementReadyEvent): void => {
    reportAvailability(getAvailableWalletMethods(event.availablePaymentMethods))
  }

  const handleAvailablePaymentMethodsChange = (
    event: StripeExpressCheckoutElementAvailablePaymentMethodsChangeEvent,
  ): void => {
    reportAvailability(getChangedWalletMethods(event.paymentMethods))
  }

  const handleLoadError = (): void => {
    onWalletAvailabilityChange?.([])
  }

  const handleCancel = (): void => {
    onWalletProcessingChange?.(false)
  }

  const handleConfirm = async (
    event: StripeExpressCheckoutElementConfirmEvent,
  ): Promise<void> => {
    if (!stripe || !elements) {
      event.paymentFailed({ reason: 'fail' })
      return
    }

    onWalletProcessingChange?.(true)

    const { error: submitError } = await elements.submit()

    if (submitError) {
      event.paymentFailed({ reason: 'fail' })
      onWalletProcessingChange?.(false)
      onWalletPaymentError()
      return
    }

    const session = await onConfirmWalletPayment(event.expressPaymentType)

    if (!session) {
      event.paymentFailed({ reason: 'fail' })
      onWalletProcessingChange?.(false)
      return
    }

    const { error: confirmError } = await stripe.confirmPayment({
      elements,
      clientSecret: session.clientSecret,
      confirmParams: { return_url: session.returnUrl },
      redirect: 'if_required',
    })

    if (confirmError) {
      event.paymentFailed({ reason: 'fail' })
      onWalletProcessingChange?.(false)
      onWalletPaymentError()
      return
    }

    onWalletProcessingChange?.(false)
    onWalletPaymentSuccess()
  }

  const suppressedWalletMethod = isAvailabilityReported
    ? selectedWalletMethod
    : void 0

  return (
    <Wrapper>
      <ExpressCheckoutElement
        key={suppressedWalletMethod}
        options={getWalletButtonOptions(resolvedTheme, suppressedWalletMethod)}
        onReady={handleReady}
        onAvailablePaymentMethodsChange={handleAvailablePaymentMethodsChange}
        onLoadError={handleLoadError}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
      />
    </Wrapper>
  )
}
